import type { Branding } from "../types";
import type { YbugColors } from "./ybug";

/**
 * Skórka marki w procesie kandydata.
 *
 * Marka to wyłącznie wartości tokenów — nazwy z tokens.css (`--primary`,
 * `--muted`…) są kontraktem, więc żaden arkusz nie wie, że marka istnieje.
 * Backend liczy komplet dla obu motywów (`branding.tokens`), a tutaj tylko
 * wkładamy go jednym <style> do <head>.
 *
 * Selektory są celowo mocniejsze niż w tokens.css: `:root:not([data-theme="dark"])`
 * nie dotyka motywu ciemnego (tam neutrale zostają grafitowe z for apps),
 * a `:root[data-theme="dark"]` wygrywa z `[data-theme="dark"]` tylko tymi
 * tokenami, które marka liczy dla ciemnego.
 *
 * Classic nie ma tokenów — <style> znika i proces wygląda jak przed szablonami.
 */

const STYL = "marka";

/** Klucz zapamiętanej skórki — czyta go skrypt w index.html przed pierwszym malowaniem. */
const klucz = (recordId: string) => `marka.${recordId}`;

/** Zapis dla index.html: CSS i logo, żeby powrót do linku nie mrugał barwami for apps. */
export interface ZapamietanaMarka {
  css: string | null;
  logo: string | null;
  logoDark: string | null;
}

function blok(selektor: string, tokeny: Record<string, string>): string {
  const deklaracje = Object.entries(tokeny)
    .map(([nazwa, wartosc]) => `--${nazwa}:${wartosc}`)
    .join(";");
  return `${selektor}{${deklaracje}}`;
}

export function cssMarki(branding: Branding): string | null {
  if (!branding.tokens) return null;
  return (
    blok(':root:not([data-theme="dark"])', branding.tokens.light) +
    blok(':root[data-theme="dark"]', branding.tokens.dark)
  );
}

/** Kolor paska przeglądarki idzie za płótnem — inaczej nad stroną widać szew. */
export function odswiezPasek(): void {
  const plotno = getComputedStyle(document.documentElement).getPropertyValue("--canvas").trim();
  if (plotno) document.querySelector('meta[name="theme-color"]')?.setAttribute("content", plotno);
}

export function zastosujMarke(recordId: string, branding: Branding): void {
  const css = cssMarki(branding);
  let styl = document.getElementById(STYL) as HTMLStyleElement | null;
  if (css) {
    if (!styl) {
      styl = document.createElement("style");
      styl.id = STYL;
      document.head.appendChild(styl);
    }
    styl.textContent = css;
  } else {
    styl?.remove();
  }
  odswiezPasek();

  const zapis: ZapamietanaMarka = {
    css,
    logo: branding.logo?.light ?? null,
    logoDark: branding.logo?.dark ?? branding.logo?.light ?? null,
  };
  try {
    window.localStorage.setItem(klucz(recordId), JSON.stringify(zapis));
  } catch {
    /* prywatne okno — przy następnym wejściu kolory przyjdą chwilę później */
  }
}

/** Przycisk zgłoszeń Ybug w barwach marki: tło jak przycisk akcji, napis jak na nim. */
export function koloryYbug(branding: Branding): YbugColors | null {
  if (!branding.tokens) return null;
  const { light, dark } = branding.tokens;
  return {
    light: { button: light.primary, text: light["primary-ink"] },
    dark: { button: dark.primary ?? light.primary, text: dark["primary-ink"] ?? light["primary-ink"] },
  };
}

/** Logo marki dla motywu; `null` = sygnet i wordmark for apps. */
export function logoMarki(branding: Branding | null | undefined, ciemny: boolean): string | null {
  const logo = branding?.logo;
  if (!logo) return null;
  return (ciemny ? logo.dark : null) ?? logo.light ?? logo.dark;
}
